
import React, { Component, ErrorInfo, ReactNode } from "react";
import { createLogger } from "@/lib/utils/logger";

const logger = createLogger('frontend', 'ErrorBoundary');

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error(
      `Uncaught error: ${error.message}`,
      JSON.stringify({
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      })
    );
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background">
          <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-lg border border-primary/10">
            <h2 className="text-xl font-title text-primary mb-4">Something went wrong</h2>
            <p className="text-muted-foreground font-body mb-4">
              The application encountered an unexpected error. Please try refreshing the page or contact support if the issue persists.
            </p>
            <div className="bg-muted p-3 rounded-md mb-4 overflow-auto max-h-48">
              <pre className="text-xs">{this.state.error?.message || "Unknown error"}</pre>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary w-full"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
